import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Send, CheckCircle2, AlertCircle } from "lucide-react";
import {
  getEmailSettings,
  listClients,
  sendTestEmail,
  setSmtpPassword,
  upsertEmailSettings,
} from "@/lib/api";

const TRIGGERS = [
  ["send_booking_created",     "Booking created"],
  ["send_booking_approved",    "Booking approved"],
  ["send_booking_rejected",    "Booking rejected"],
  ["send_booking_reminder",    "Booking reminder (N hrs before)"],
  ["send_driver_assigned",     "Driver assigned"],
  ["send_checkin_confirmation","Driver check-in"],
  ["send_booking_completed",   "Booking completed"],
  ["send_waitlist_offer",      "Waitlist offer"],
];

const DEFAULTS = {
  smtp_host: "",
  smtp_port: 587,
  smtp_username: "",
  smtp_encryption: "tls",
  from_email: "",
  from_name: "",
  reply_to_email: "",
  reminder_hours_before: 24,
  is_configured: false,
  ...Object.fromEntries(TRIGGERS.map(([k]) => [k, true])),
};

export default function EmailSettings() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [v, setV] = useState(DEFAULTS);
  const [pw, setPw] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listClients();
      if (cancelled) return;
      setClients(data || []);
      if ((data || []).length) setClientId((cur) => cur || data[0].id);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!clientId) return;
    (async () => {
      const { data } = await getEmailSettings(clientId);
      if (cancelled) return;
      setV(data ? { ...DEFAULTS, ...data } : { ...DEFAULTS, client_id: clientId });
      setPw("");
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const save = async () => {
    if (!clientId) return;
    setBusy(true);
    const { error } = await upsertEmailSettings({ ...v, client_id: clientId });
    if (!error && pw) {
      const r = await setSmtpPassword(clientId, pw);
      if (r.error) {
        setBusy(false);
        toast.error(r.error.message);
        return;
      }
      setPw("");
    }
    setBusy(false);
    if (error) toast.error(error.message);
    else toast.success("Email settings saved.");
  };

  const onSendTest = async () => {
    if (!testTo) {
      toast.error("Enter a test recipient email.");
      return;
    }
    setBusy(true);
    const { data, error } = await sendTestEmail(clientId, testTo);
    setBusy(false);
    if (error) toast.error(error.message);
    else toast.success(`Test email sent to ${data.sent_to}.`);
  };

  return (
    <div data-testid="email-settings-page" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Mail className="h-6 w-6" /> Email settings
          </h1>
          <p className="text-sm text-slate-500">
            Per-tenant SMTP + 8 event triggers. Passwords are encrypted in
            Postgres via pgcrypto and only readable by the FastAPI worker.
          </p>
        </div>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-72" data-testid="email-client-picker">
            <SelectValue placeholder="Pick a client" />
          </SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">SMTP server</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3">
          <Field label="Host">
            <Input value={v.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} data-testid="smtp-host" />
          </Field>
          <Field label="Port">
            <Input
              type="number"
              value={v.smtp_port || 587}
              onChange={(e) => set("smtp_port", parseInt(e.target.value, 10) || 0)}
              data-testid="smtp-port"
            />
          </Field>
          <Field label="Encryption">
            <Select value={v.smtp_encryption || "tls"} onValueChange={(x) => set("smtp_encryption", x)}>
              <SelectTrigger data-testid="smtp-encryption"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tls">STARTTLS</SelectItem>
                <SelectItem value="ssl">SSL</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Username">
            <Input value={v.smtp_username || ""} onChange={(e) => set("smtp_username", e.target.value)} data-testid="smtp-username" />
          </Field>
          <Field label="Password (leave blank to keep current)">
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              data-testid="smtp-password"
            />
          </Field>
          <div />
          <Field label="From email">
            <Input value={v.from_email || ""} onChange={(e) => set("from_email", e.target.value)} data-testid="smtp-from-email" />
          </Field>
          <Field label="From name">
            <Input value={v.from_name || ""} onChange={(e) => set("from_name", e.target.value)} data-testid="smtp-from-name" />
          </Field>
          <Field label="Reply-to">
            <Input value={v.reply_to_email || ""} onChange={(e) => set("reply_to_email", e.target.value)} data-testid="smtp-reply-to" />
          </Field>
          <Field label="Reminder hours before">
            <Input
              type="number"
              value={v.reminder_hours_before || 24}
              onChange={(e) => set("reminder_hours_before", parseInt(e.target.value, 10) || 24)}
              data-testid="smtp-reminder-hours"
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Trigger events</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-2">
          {TRIGGERS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm py-1.5">
              <Switch
                checked={!!v[k]}
                onCheckedChange={(x) => set(k, x)}
                data-testid={`trigger-${k}`}
              />
              {label}
            </label>
          ))}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-end gap-3">
        <Button onClick={save} disabled={busy} data-testid="smtp-save">
          {busy ? "Saving…" : "Save settings"}
        </Button>
        <div className="flex items-end gap-2">
          <Field label="Send test to">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@example.com"
              data-testid="smtp-test-to"
              className="w-64"
            />
          </Field>
          <Button variant="outline" onClick={onSendTest} disabled={busy || !testTo} data-testid="smtp-test-send">
            <Send className="h-4 w-4 mr-1.5" /> Send test
          </Button>
        </div>
        {v.last_test_at && (
          <div className={`text-xs flex items-center gap-1 ${v.last_test_success ? "text-emerald-700" : "text-rose-700"}`}>
            {v.last_test_success ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            Last test {new Date(v.last_test_at).toLocaleString("en-AU", { hourCycle: "h23" })}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
